/**
 * Neon Database Client Configuration
 * Serverless PostgreSQL connection for LegalSyncUp
 *
 * Connection Details:
 * - Project: syncup-brand-legal (polished-truth-90679079)
 * - Branch: legalsyncup-production (br-cool-bird-agmnaox6)
 * - Schema: legalsyncup (isolated from shared neon_auth)
 */

import { neon, Pool } from '@neondatabase/serverless';

// Note: fetchConnectionCache is deprecated (now always true by default)

/**
 * Get the database URL from environment
 * Returns null if not available (client-side in production)
 */
const getDatabaseUrl = (): string | null => {
  // Check for environment-specific URLs
  const envUrl = import.meta.env.VITE_NEON_DATABASE_URL
    || import.meta.env.NEON_DATABASE_URL
    || import.meta.env.DATABASE_URL;

  // In production client-side, this will be null
  // Database access should go through API endpoints instead
  return envUrl || null;
};

/**
 * Check if we're running in a server environment with database access
 */
const hasDbAccess = (): boolean => {
  return getDatabaseUrl() !== null;
};

/**
 * SQL query function using Neon serverless driver
 * Use for simple queries in serverless environments
 * Returns null if database URL is not available (client-side)
 */
const dbUrl = getDatabaseUrl();
export const sql = dbUrl ? neon(dbUrl) : null;

/**
 * Connection pool for more complex operations
 * Use when you need transactions or multiple queries
 * Returns null if database URL is not available (client-side)
 */
export const pool = dbUrl ? new Pool({ connectionString: dbUrl }) : null;

/**
 * Check if database is available
 */
export const isDatabaseAvailable = (): boolean => hasDbAccess();

/**
 * Schema name for LegalSyncUp application data
 * All application tables are in this schema, separate from neon_auth
 */
export const LEGALSYNCUP_SCHEMA = 'legalsyncup';

/**
 * Auth schema name for Better Auth tables
 * Shared across all syncup projects
 */
export const AUTH_SCHEMA = 'neon_auth';

/**
 * Helper to qualify table names with the legalsyncup schema
 */
export const table = (tableName: string): string => {
  return `${LEGALSYNCUP_SCHEMA}.${tableName}`;
};

/**
 * Execute a query with the legalsyncup schema search path
 * Throws error if database is not available (client-side)
 */
export async function query<T>(
  queryText: string,
  params?: unknown[],
  tenantId?: string
): Promise<T[]> {
  if (!pool) {
    throw new Error('Database not available. Use API endpoints instead.');
  }
  const client = await pool.connect();
  try {
    // Wrap in a transaction so SET LOCAL / set_config(true) actually takes effect.
    // Outside a transaction block, set_config(name, value, true) is a no-op.
    await client.query('BEGIN');
    await client.query(
      `SET LOCAL search_path TO ${LEGALSYNCUP_SCHEMA}, ${AUTH_SCHEMA}, public`
    );
    if (tenantId) {
      // Transaction-local: resets after COMMIT, so no contamination across pool connections.
      await client.query(
        `SELECT set_config('app.current_tenant_id', $1, true)`,
        [tenantId]
      );
    }
    const result = await client.query(queryText, params);
    await client.query('COMMIT');
    return result.rows as T[];
  } catch (error) {
    await client.query('ROLLBACK');
    throw error;
  } finally {
    client.release();
  }
}

/**
 * Execute a transaction with multiple queries
 * Throws error if database is not available (client-side)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PoolClient = any;
export async function transaction<T>(
  callback: (client: PoolClient) => Promise<T>,
  tenantId?: string
): Promise<T> {
  if (!pool) {
    throw new Error('Database not available. Use API endpoints instead.');
  }
  const client = await pool.connect();
  try {
    await client.query('BEGIN');
    await client.query(
      `SET LOCAL search_path TO ${LEGALSYNCUP_SCHEMA}, ${AUTH_SCHEMA}, public`
    );
    if (tenantId) {
      await client.query(
        `SELECT set_config('app.current_tenant_id', $1, true)`,
        [tenantId]
      );
    }
    const result = await callback(client);
    await client.query('COMMIT');
    return result;
  } catch (error) {
    await client.query('ROLLBACK');
    throw error;
  } finally {
    client.release();
  }
}

/**
 * Health check for database connectivity
 */
export async function healthCheck(): Promise<{ connected: boolean; latency: number; error?: string }> {
  const start = Date.now();
  if (!sql) {
    return {
      connected: false,
      latency: 0,
      error: 'Database not available (client-side)',
    };
  }
  try {
    await sql`SELECT 1 as health`;
    return {
      connected: true,
      latency: Date.now() - start,
    };
  } catch (error) {
    return {
      connected: false,
      latency: Date.now() - start,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export default sql;
